import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail, ShieldCheck, KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 email, 2 otp, 3 new password, 4 done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sentInfo, setSentInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendCode = async (isResend = false) => {
    setError("");
    setLoading(true);
    const toastId = toast.loading(isResend ? "Resending OTP..." : "Sending OTP...");
    try {
      const url = isResend ? "/auth/resend-otp" : "/auth/forgot-password";
      const { data } = await api.post(url, { email });
      setSentInfo(data);
      setStep(2);
      setCooldown(60);
      toast.success(`OTP sent to your email (${data.email}). Check your inbox.`, { id: toastId });
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || "Failed to send OTP. Please try again.";
      setError(msg);
      toast.error(msg, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      setResetToken(data.reset_token);
      setStep(3);
      toast.success("OTP verified. Set your new password.");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || "Verification failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { reset_token: resetToken, new_password: password });
      setStep(4);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title="Reset Password — The Pataudi" description="Reset your The Pataudi account password securely via OTP." />
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-card rounded-card shadow-soft border border-border p-7" data-testid="forgot-card">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4"><ArrowLeft className="h-4 w-4" /> Back to login</Link>

          {/* Progress dots */}
          <div className="flex items-center gap-2 mb-5">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`h-1.5 flex-1 rounded-full ${step >= n ? "bg-primary" : "bg-border"}`} />
            ))}
          </div>

          {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="forgot-error">{error}</div>}

          {step === 1 && (
            <div data-testid="forgot-step-email">
              <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary"><KeyRound className="h-5 w-5" /></div>
              <h1 className="mt-3 font-display font-bold text-2xl text-ink">Forgot password?</h1>
              <p className="text-sm text-muted-foreground mt-1">Enter your account email. We'll send a 6-digit verification code to your inbox.</p>
              <div className="mt-5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your registered email" className="mt-1.5 rounded-sm" data-testid="forgot-email" />
              </div>
              <Button onClick={() => sendCode(false)} disabled={loading || !email} className="w-full mt-5 bg-primary hover:bg-primary/90 text-white rounded-sm h-11" data-testid="send-code-btn">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending OTP...</> : "Send Verification Code"}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="forgot-step-otp">
              <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary"><ShieldCheck className="h-5 w-5" /></div>
              <h1 className="mt-3 font-display font-bold text-2xl text-ink">Enter the code</h1>
              <p className="text-sm text-muted-foreground mt-1">
                We sent a 6-digit code to{" "}
                <span className="inline-flex items-center gap-1 font-medium text-ink"><Mail className="h-3.5 w-3.5" /> {sentInfo?.email}</span>.
              </p>
              {(!sentInfo?.channels || sentInfo.channels.length === 0) && (
                <p className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-2.5 py-1.5" data-testid="forgot-channels-warn">Email delivery status unknown. If you don't receive the code in 60s, try resend or contact support.</p>
              )}
              <div className="mt-5">
                <Label htmlFor="otp">Verification Code</Label>
                <Input id="otp" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="••••••" className="mt-1.5 rounded-sm tracking-[0.5em] text-center text-lg" data-testid="forgot-otp" />
              </div>
              <Button onClick={verify} disabled={loading || otp.length !== 6} className="w-full mt-5 bg-primary hover:bg-primary/90 text-white rounded-sm h-11" data-testid="verify-otp-btn">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Verifying...</> : "Verify Code"}
              </Button>
              <div className="mt-4 text-center text-sm">
                {cooldown > 0 ? (
                  <span className="text-muted-foreground" data-testid="resend-cooldown">Resend code in {cooldown}s</span>
                ) : (
                  <button onClick={() => sendCode(true)} className="text-accent font-medium hover:underline" data-testid="resend-otp-btn">Resend code</button>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="forgot-step-password">
              <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary"><KeyRound className="h-5 w-5" /></div>
              <h1 className="mt-3 font-display font-bold text-2xl text-ink">Set a new password</h1>
              <p className="text-sm text-muted-foreground mt-1">Choose a strong password you'll remember.</p>
              <div className="mt-5 space-y-4">
                <div>
                  <Label htmlFor="np">New Password</Label>
                  <Input id="np" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="mt-1.5 rounded-sm" data-testid="new-password" />
                </div>
                <div>
                  <Label htmlFor="cp">Confirm Password</Label>
                  <Input id="cp" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password" className="mt-1.5 rounded-sm" data-testid="confirm-password" />
                </div>
              </div>
              <Button onClick={reset} disabled={loading} className="w-full mt-5 bg-primary hover:bg-primary/90 text-white rounded-sm h-11" data-testid="reset-password-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
              </Button>
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-4" data-testid="forgot-done">
              <CheckCircle2 className="h-14 w-14 text-success mx-auto" />
              <h1 className="mt-4 font-display font-bold text-2xl text-ink">Password reset!</h1>
              <p className="text-sm text-muted-foreground mt-1">Your password has been updated. You can now log in with your new password.</p>
              <Button onClick={() => navigate("/login")} className="w-full mt-6 bg-primary hover:bg-primary/90 text-white rounded-sm h-11" data-testid="go-to-login-btn">Go to Login</Button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
